import { PUBLICATION_JOURNAL_FORMATS_READ } from '@streamlain/doc-publication-contract';
import {
  type EncryptedRecord,
  localAccountKeyError,
  type LocalDataKind,
  type LocalDataScope,
} from '@streamlain/view-config/local-data-seal';

import {
  INDEXER_TABLES,
  type IndexerDocument,
  IndexerSchema,
  isIndexerTable,
} from '../storage/indexer';

export interface LocalDataContainer {
  keyScopeId: string;
  revision: string;
  sealed: EncryptedRecord;
}
export interface LocalDocKeyRecord {
  version: 1;
  accountId: string;
  workspaceId: string;
  keyScopeId: string;
  keyId: string;
  wrappedDEK: Uint8Array;
  registryRevision: string;
}
export const docKeyScope = (docId: string) => JSON.stringify(['doc', docId]);
export const blobKeyScope = (blobKey: string) =>
  JSON.stringify(['blob', blobKey]);
export function localPayloadScope(
  kind: LocalDataKind,
  docId: string,
  operationId?: string
) {
  if (kind === 'update' || kind === 'snapshot') return docKeyScope(docId);
  if (kind === 'publication-member')
    return JSON.stringify(['member', docId, operationId]);
  if (kind === 'journal')
    return JSON.stringify(['journal', docId, operationId]);
  if (kind === 'blob' || kind === 'blob-metadata') return blobKeyScope(docId);
  throw localAccountKeyError('CORRUPT');
}
export function localIndexScope(
  ownerDocId: string,
  table: string,
  recordId: string
) {
  if (!ownerDocId || !recordId || !isIndexerTable(table))
    throw localAccountKeyError('CORRUPT');
  return JSON.stringify(['index', ownerDocId, table, recordId]);
}
export function localIndexDocumentScope<S extends keyof IndexerSchema>(
  table: string,
  document: IndexerDocument<S>
) {
  const ids = document.fields.get('docId');
  if (!ids || ids.length !== 1) throw localAccountKeyError('CORRUPT');
  return localIndexScope(ids[0], table, document.id);
}
export function encodeLocalJson(value: unknown) {
  return new TextEncoder().encode(JSON.stringify(value));
}
export function decodeLocalJson(bytes: Uint8Array): unknown {
  try {
    return JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes));
  } catch {
    throw localAccountKeyError('CORRUPT');
  }
}
/**
 * Read check of a persisted journal entry: a foreign tag means a newer app
 * wrote it, a missing tag means damage.
 */
export function assertPublicationJournalFormat(
  formatEpoch: unknown
): asserts formatEpoch is string {
  if (typeof formatEpoch !== 'string') throw localAccountKeyError('CORRUPT');
  if (
    !(PUBLICATION_JOURNAL_FORMATS_READ as readonly string[]).includes(
      formatEpoch
    )
  )
    throw localAccountKeyError('LOCAL_STORE_NEWER');
}
/**
 * Companions of a journal entry read under its tag: an entry of the first tag
 * has none, a newer one names them.
 */
export function journalCompanions<T>(
  formatEpoch: string,
  companions: readonly T[] | undefined
): readonly T[] {
  if (formatEpoch === PUBLICATION_JOURNAL_FORMATS_READ[0]) return [];
  if (!Array.isArray(companions)) throw localAccountKeyError('CORRUPT');
  return companions;
}
export function encodeLocalDoc(
  bin: Uint8Array,
  meta: { editor?: string; requestId?: string } = {}
) {
  const header = encodeLocalJson(meta);
  const bytes = new Uint8Array(4 + header.length + bin.length);
  new DataView(bytes.buffer).setUint32(0, header.length);
  bytes.set(header, 4);
  bytes.set(bin, 4 + header.length);
  return bytes;
}
export function decodeLocalDoc(bytes: Uint8Array): {
  bin: Uint8Array;
  editor?: string;
  requestId?: string;
} {
  if (bytes.length < 4) throw localAccountKeyError('CORRUPT');
  const length = new DataView(
    bytes.buffer,
    bytes.byteOffset,
    bytes.byteLength
  ).getUint32(0);
  if (length > bytes.length - 4) throw localAccountKeyError('CORRUPT');
  const meta = decodeLocalJson(bytes.subarray(4, 4 + length));
  if (
    !meta ||
    typeof meta !== 'object' ||
    Array.isArray(meta) ||
    Object.keys(meta).some(key => key !== 'editor' && key !== 'requestId')
  )
    throw localAccountKeyError('CORRUPT');
  const { editor, requestId } = meta as {
    editor?: unknown;
    requestId?: unknown;
  };
  if (
    (editor !== undefined && typeof editor !== 'string') ||
    (requestId !== undefined && typeof requestId !== 'string')
  )
    throw localAccountKeyError('CORRUPT');
  return {
    bin: bytes.slice(4 + length),
    ...(editor === undefined ? {} : { editor }),
    ...(requestId === undefined ? {} : { requestId }),
  };
}
export function encodeLocalIndex(data: Map<string, string[]>) {
  return encodeLocalJson([...data]);
}
export function decodeLocalIndex(bytes: Uint8Array): Map<string, string[]> {
  const value = decodeLocalJson(bytes);
  if (
    !Array.isArray(value) ||
    value.some(
      entry =>
        !Array.isArray(entry) ||
        entry.length !== 2 ||
        typeof entry[0] !== 'string' ||
        !Array.isArray(entry[1]) ||
        entry[1].some(item => typeof item !== 'string')
    ) ||
    new Set(value.map(entry => entry[0])).size !== value.length
  )
    throw localAccountKeyError('CORRUPT');
  return new Map(value);
}
export function containerScope(
  scope: Omit<LocalDataScope, 'revision'>,
  payload: LocalDataContainer
): LocalDataScope {
  if (
    !payload ||
    typeof payload.revision !== 'string' ||
    !payload.revision ||
    typeof payload.keyScopeId !== 'string'
  )
    throw localAccountKeyError('CORRUPT');
  return { ...scope, revision: payload.revision };
}

/** Bounded native wire container. Only TS interprets this header. */
const MAX_LOCAL_CONTAINER_HEADER = 16384;
export function encodeLocalContainer(payload: LocalDataContainer): Uint8Array {
  const header = encodeLocalJson([
    1,
    payload.keyScopeId,
    payload.revision,
    payload.sealed.keyId,
    [...payload.sealed.iv],
  ]);
  if (header.length > MAX_LOCAL_CONTAINER_HEADER)
    throw localAccountKeyError('CORRUPT');
  const bytes = new Uint8Array(
    4 + header.length + payload.sealed.ciphertext.length
  );
  new DataView(bytes.buffer).setUint32(0, header.length);
  bytes.set(header, 4);
  bytes.set(payload.sealed.ciphertext, 4 + header.length);
  return bytes;
}
export function decodeLocalContainer(bytes: Uint8Array): LocalDataContainer {
  if (!(bytes instanceof Uint8Array) || bytes.length < 4)
    throw localAccountKeyError('CORRUPT');
  const length = new DataView(
    bytes.buffer,
    bytes.byteOffset,
    bytes.byteLength
  ).getUint32(0);
  if (length > MAX_LOCAL_CONTAINER_HEADER || length > bytes.length - 20)
    throw localAccountKeyError('CORRUPT');
  const header = decodeLocalJson(bytes.subarray(4, 4 + length));
  if (
    !Array.isArray(header) ||
    header.length !== 5 ||
    header[0] !== 1 ||
    header.slice(1, 4).some(v => typeof v !== 'string' || !v) ||
    !Array.isArray(header[4]) ||
    header[4].length !== 12 ||
    header[4].some(v => !Number.isInteger(v) || v < 0 || v > 255)
  )
    throw localAccountKeyError('CORRUPT');
  return {
    keyScopeId: header[1],
    revision: header[2],
    sealed: {
      version: 1,
      keyId: header[3],
      iv: Uint8Array.from(header[4]),
      ciphertext: bytes.slice(4 + length),
    },
  };
}

/** Owner S4b2 hard batch contract; an oversized individual object fails closed. */
export const MAX_LOCAL_INDEX_BATCH_RECORDS = 128;
export const MAX_LOCAL_INDEX_BATCH_BYTES = 16 * 1024 * 1024;
export interface LocalIndexSegmentIdentity {
  segmentId: string;
  role: 'record' | 'metadata';
  table: keyof IndexerSchema;
  recordId: string;
  ownerDocId: string;
  nid: number;
  storageRevision: string;
}
export interface LocalIndexSegment extends LocalIndexSegmentIdentity {
  recordKey?: [string, string];
  keyScopeId: string;
  payload: LocalDataContainer;
}
export interface LocalIndexState {
  key: 'state';
  revision: string;
  segmentId: string;
}
/** One sealed postings block as the head metadata lists it. */
export interface LocalIndexBlockRef {
  segmentId: string;
  /**
   * Key names of the block's first and last posting. Key names sort like
   * indexedDB.cmp on [table, key], so a lookup reads only blocks in range.
   */
  first: string;
  last: string;
  count: number;
  /** Sealed bytes of the stored row. */
  bytes: number;
}
/**
 * The head metadata. Block references live in sealed run parts: each run is
 * a chain of parts, named by its last part, so the head stays as small as
 * the run count however many blocks the index holds.
 */
export interface LocalIndexHistory {
  nextNid: number;
  nextPosting: number;
  recordCount: number;
  metadata: [string, number][];
  /** Last part of each run, oldest run first; a run sorts by key name, then posting id. */
  runs: string[];
  /** Last part of each unfinished chain: stored, but in no run yet. */
  pending: string[];
  /** Last part of each chain of replaced runs, still to delete with its blocks. */
  garbage: string[];
  /** Sealed bytes of every row of the index except the head metadata row. */
  sealedBytes: number;
  /** Records removed since the last full merge; their postings stay in the runs. */
  deadRecords: number;
}
/** One part of a run: its blocks in key order and the part before it. */
export interface LocalIndexRunPart {
  prev: string | null;
  blocks: LocalIndexBlockRef[];
}
/**
 * A sealed postings block or run part, written once: no nid and no record
 * key, so no record index sees it.
 */
export interface LocalIndexBlockIdentity {
  segmentId: string;
  role: 'postings' | 'run';
  ownerDocId: string;
  /** Blocks are written once: the revision is the segment id. */
  storageRevision: string;
}
export interface LocalIndexBlock extends LocalIndexBlockIdentity {
  keyScopeId: string;
  payload: LocalDataContainer;
}
export type LocalIndexRow = LocalIndexSegment | LocalIndexBlock;
/** A posting of a block: posting id, key name, record nid, flat positions. */
export type LocalIndexPosting = [
  id: number,
  name: string,
  nid: number,
  pos: number[] | null,
];
export function localIndexBlockScope(workspaceId: string, segmentId: string) {
  if (!workspaceId || !segmentId) throw localAccountKeyError('CORRUPT');
  return JSON.stringify(['index-block', workspaceId, segmentId]);
}
const safeCount = (value: unknown, min: number): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= min;
/** `table` + U+0000 + one char per key byte, of a table of the index. */
function validPostingName(name: unknown): name is string {
  if (typeof name !== 'string') return false;
  const split = name.indexOf('\u0000');
  if (split < 1 || !isIndexerTable(name.slice(0, split))) return false;
  for (let at = split + 1; at < name.length; at++)
    if (name.charCodeAt(at) > 0xff) return false;
  return true;
}
/** Run part ids share this prefix, so one key range reads every part. */
export const LOCAL_INDEX_RUN_PART_PREFIX = 'run:';
export const localIndexRunPartId = () =>
  `${LOCAL_INDEX_RUN_PART_PREFIX}${crypto.randomUUID()}`;
const isRunPartId = (id: string) =>
  id.startsWith(LOCAL_INDEX_RUN_PART_PREFIX) &&
  id.length > LOCAL_INDEX_RUN_PART_PREFIX.length;
function validateBlockIdentity(
  identity: LocalIndexBlockIdentity,
  role: LocalIndexBlockIdentity['role']
) {
  if (
    !identity.segmentId ||
    identity.role !== role ||
    isRunPartId(identity.segmentId) !== (role === 'run') ||
    !identity.ownerDocId ||
    identity.storageRevision !== identity.segmentId
  )
    throw localAccountKeyError('CORRUPT');
}
export function encodeLocalIndexBlock(
  identity: LocalIndexBlockIdentity,
  postings: readonly LocalIndexPosting[]
) {
  validateBlockIdentity(identity, 'postings');
  // Postings of one key name share one entry: [name, [id, nid, pos][]].
  const keys: [string, [number, number, number[] | null][]][] = [];
  for (const [id, name, nid, pos] of postings) {
    const last = keys.at(-1);
    if (last?.[0] === name) last[1].push([id, nid, pos]);
    else keys.push([name, [[id, nid, pos]]]);
  }
  const bytes = encodeLocalJson({ version: 1, ...identity, keys });
  try {
    decodeLocalIndexBlock(bytes, identity);
    return bytes;
  } catch (error) {
    bytes.fill(0);
    throw error;
  }
}
export function decodeLocalIndexBlock(
  bytes: Uint8Array,
  identity: LocalIndexBlockIdentity
): LocalIndexPosting[] {
  validateBlockIdentity(identity, 'postings');
  if (bytes.length > MAX_LOCAL_INDEX_BATCH_BYTES)
    throw localAccountKeyError('CORRUPT');
  const value = decodeLocalJson(bytes);
  if (
    !indexObject(value) ||
    value.version !== 1 ||
    value.segmentId !== identity.segmentId ||
    value.role !== identity.role ||
    value.ownerDocId !== identity.ownerDocId ||
    value.storageRevision !== identity.storageRevision ||
    !Array.isArray(value.keys) ||
    value.keys.length === 0
  )
    throw localAccountKeyError('CORRUPT');
  // Names strictly ascend; ids strictly ascend within a name.
  const postings: LocalIndexPosting[] = [];
  let previousName: string | null = null;
  for (const key of value.keys) {
    if (
      !Array.isArray(key) ||
      key.length !== 2 ||
      !validPostingName(key[0]) ||
      (previousName !== null && key[0] <= previousName) ||
      !Array.isArray(key[1]) ||
      key[1].length === 0
    )
      throw localAccountKeyError('CORRUPT');
    const name: string = key[0];
    previousName = name;
    let previousId = 0;
    for (const entry of key[1]) {
      if (
        !Array.isArray(entry) ||
        entry.length !== 3 ||
        !safeCount(entry[0], previousId + 1) ||
        !safeCount(entry[1], 0) ||
        (entry[2] !== null &&
          (!Array.isArray(entry[2]) ||
            entry[2].some((at: unknown) => !safeCount(at, 0))))
      )
        throw localAccountKeyError('CORRUPT');
      previousId = entry[0];
      postings.push([entry[0], name, entry[1], entry[2]]);
    }
  }
  return postings;
}
type EncodedBlockRef = [string, string, string, number, number];
const encodeBlockRef = (ref: LocalIndexBlockRef): EncodedBlockRef => [
  ref.segmentId,
  ref.first,
  ref.last,
  ref.count,
  ref.bytes,
];
function decodeBlockRef(value: unknown, seen: Set<string>): LocalIndexBlockRef {
  if (
    !Array.isArray(value) ||
    value.length !== 5 ||
    typeof value[0] !== 'string' ||
    !value[0] ||
    seen.has(value[0]) ||
    !validPostingName(value[1]) ||
    !validPostingName(value[2]) ||
    value[2] < value[1] ||
    !safeCount(value[3], 1) ||
    !safeCount(value[4], 1)
  )
    throw localAccountKeyError('CORRUPT');
  seen.add(value[0]);
  return {
    segmentId: value[0],
    first: value[1],
    last: value[2],
    count: value[3],
    bytes: value[4],
  };
}
export function encodeLocalIndexRunPart(
  identity: LocalIndexBlockIdentity,
  part: LocalIndexRunPart
) {
  validateBlockIdentity(identity, 'run');
  const bytes = encodeLocalJson({
    version: 1,
    ...identity,
    prev: part.prev,
    blocks: part.blocks.map(encodeBlockRef),
  });
  try {
    decodeLocalIndexRunPart(bytes, identity);
    return bytes;
  } catch (error) {
    bytes.fill(0);
    throw error;
  }
}
export function decodeLocalIndexRunPart(
  bytes: Uint8Array,
  identity: LocalIndexBlockIdentity
): LocalIndexRunPart {
  validateBlockIdentity(identity, 'run');
  if (bytes.length > MAX_LOCAL_INDEX_BATCH_BYTES)
    throw localAccountKeyError('CORRUPT');
  const value = decodeLocalJson(bytes);
  if (
    !indexObject(value) ||
    value.version !== 1 ||
    value.segmentId !== identity.segmentId ||
    value.role !== identity.role ||
    value.ownerDocId !== identity.ownerDocId ||
    value.storageRevision !== identity.storageRevision ||
    (value.prev !== null &&
      (typeof value.prev !== 'string' ||
        !isRunPartId(value.prev) ||
        value.prev === identity.segmentId)) ||
    !Array.isArray(value.blocks) ||
    value.blocks.length === 0
  )
    throw localAccountKeyError('CORRUPT');
  const seen = new Set<string>();
  const blocks = value.blocks.map(ref => decodeBlockRef(ref, seen));
  for (let at = 1; at < blocks.length; at++)
    if (blocks[at].first < blocks[at - 1].last)
      throw localAccountKeyError('CORRUPT');
  return { prev: value.prev, blocks };
}
function decodePartIds(value: unknown, seen: Set<string>) {
  if (!Array.isArray(value)) throw localAccountKeyError('CORRUPT');
  for (const id of value) {
    if (typeof id !== 'string' || !isRunPartId(id) || seen.has(id))
      throw localAccountKeyError('CORRUPT');
    seen.add(id);
  }
  return value as string[];
}
function indexObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}
function validateIndexIdentity(identity: LocalIndexSegmentIdentity) {
  if (
    !identity.segmentId ||
    !identity.recordId ||
    !identity.ownerDocId ||
    !identity.storageRevision ||
    !isIndexerTable(identity.table) ||
    !Number.isSafeInteger(identity.nid) ||
    (identity.role === 'record'
      ? identity.nid < 1
      : identity.role !== 'metadata' || identity.nid !== 0)
  )
    throw localAccountKeyError('CORRUPT');
}
function decodeIndexEnvelope(
  bytes: Uint8Array,
  identity: LocalIndexSegmentIdentity,
  version = 1
) {
  validateIndexIdentity(identity);
  if (bytes.length > MAX_LOCAL_INDEX_BATCH_BYTES)
    throw localAccountKeyError('CORRUPT');
  const value = decodeLocalJson(bytes);
  if (
    !indexObject(value) ||
    value.version !== version ||
    value.segmentId !== identity.segmentId ||
    value.role !== identity.role ||
    value.table !== identity.table ||
    value.recordId !== identity.recordId ||
    value.ownerDocId !== identity.ownerDocId ||
    value.nid !== identity.nid ||
    value.storageRevision !== identity.storageRevision
  )
    throw localAccountKeyError('CORRUPT');
  return value;
}
export function encodeLocalIndexRecord(
  identity: LocalIndexSegmentIdentity,
  data: Map<string, string[]>
) {
  validateIndexIdentity(identity);
  if (identity.role !== 'record') throw localAccountKeyError('CORRUPT');
  for (const [key, values] of data)
    if (
      !Object.hasOwn(IndexerSchema[identity.table], key) ||
      !Array.isArray(values) ||
      values.some(value => typeof value !== 'string')
    )
      throw localAccountKeyError('CORRUPT');
  const docIds = data.get('docId');
  if (!docIds || docIds.length !== 1 || docIds[0] !== identity.ownerDocId)
    throw localAccountKeyError('CORRUPT');
  return encodeLocalJson({ version: 1, ...identity, data: [...data] });
}
export function decodeLocalIndexRecord(
  bytes: Uint8Array,
  identity: LocalIndexSegmentIdentity
) {
  const value = decodeIndexEnvelope(bytes, identity);
  if (identity.role !== 'record' || !Array.isArray(value.data))
    throw localAccountKeyError('CORRUPT');
  const data = new Map<string, string[]>();
  for (const entry of value.data) {
    if (
      !Array.isArray(entry) ||
      entry.length !== 2 ||
      typeof entry[0] !== 'string' ||
      !Object.hasOwn(IndexerSchema[identity.table], entry[0]) ||
      data.has(entry[0]) ||
      !Array.isArray(entry[1])
    )
      throw localAccountKeyError('CORRUPT');
    const fields: string[] = [];
    for (const field of entry[1]) {
      if (typeof field !== 'string') throw localAccountKeyError('CORRUPT');
      fields.push(field);
    }
    data.set(entry[0], fields);
  }
  const docIds = data.get('docId');
  if (!docIds || docIds.length !== 1 || docIds[0] !== identity.ownerDocId)
    throw localAccountKeyError('CORRUPT');
  return data;
}
export function encodeLocalIndexHistory(
  identity: LocalIndexSegmentIdentity,
  history: LocalIndexHistory
) {
  validateIndexIdentity(identity);
  const bytes = encodeLocalJson({
    version: INDEX_HISTORY_VERSION,
    ...identity,
    ...history,
  });
  try {
    // The head metadata is validated by its own decoder before commit.
    decodeLocalIndexHistory(bytes, identity);
    return bytes;
  } catch (error) {
    bytes.fill(0);
    throw error;
  }
}
/** Statistics the index keeps: two per full-text field of each table. */
const FULL_TEXT_METADATA_KEYS = new Set(
  INDEXER_TABLES.flatMap(table =>
    Object.entries(IndexerSchema[table])
      .filter(
        ([, type]) =>
          (typeof type === 'string' ? type : type.type) === 'FullText'
      )
      .flatMap(([field]) =>
        ['field-count', 'avg-field-length'].map(
          stat => `full-text:${stat}:${table}:${field}`
        )
      )
  )
);
/**
 * Version 1 heads held records only and rebuilt every posting in RAM;
 * version 2 names the runs of sealed postings blocks by their last part.
 */
const INDEX_HISTORY_VERSION = 2;
/** A version 1 head of this index: its rows cannot be read as blocks. */
export function isPreviousLocalIndexHistory(
  bytes: Uint8Array,
  identity: LocalIndexSegmentIdentity
) {
  try {
    decodeIndexEnvelope(bytes, identity, 1);
    return identity.role === 'metadata';
  } catch {
    return false;
  }
}
export function decodeLocalIndexHistory(
  bytes: Uint8Array,
  identity: LocalIndexSegmentIdentity
): LocalIndexHistory {
  const value = decodeIndexEnvelope(bytes, identity, INDEX_HISTORY_VERSION);
  if (
    identity.role !== 'metadata' ||
    typeof value.nextNid !== 'number' ||
    !Number.isSafeInteger(value.nextNid) ||
    value.nextNid < 1 ||
    typeof value.nextPosting !== 'number' ||
    !Number.isSafeInteger(value.nextPosting) ||
    value.nextPosting < 1 ||
    typeof value.recordCount !== 'number' ||
    !Number.isSafeInteger(value.recordCount) ||
    value.recordCount < 0 ||
    !Array.isArray(value.metadata) ||
    !Array.isArray(value.runs) ||
    !safeCount(value.sealedBytes, 0) ||
    !safeCount(value.deadRecords, 0)
  )
    throw localAccountKeyError('CORRUPT');
  const seenParts = new Set<string>();
  const runs = decodePartIds(value.runs, seenParts);
  const pending = decodePartIds(value.pending, seenParts);
  const garbage = decodePartIds(value.garbage, seenParts);
  const metadata: [string, number][] = [];
  const seen = new Set<string>();
  for (const entry of value.metadata) {
    if (
      !Array.isArray(entry) ||
      entry.length !== 2 ||
      typeof entry[0] !== 'string' ||
      !FULL_TEXT_METADATA_KEYS.has(entry[0]) ||
      seen.has(entry[0]) ||
      typeof entry[1] !== 'number' ||
      !Number.isFinite(entry[1]) ||
      entry[1] < 0
    )
      throw localAccountKeyError('CORRUPT');
    seen.add(entry[0]);
    metadata.push([entry[0], entry[1]]);
  }
  return {
    nextNid: value.nextNid,
    nextPosting: value.nextPosting,
    recordCount: value.recordCount,
    metadata,
    runs,
    pending,
    garbage,
    sealedBytes: value.sealedBytes,
    deadRecords: value.deadRecords,
  };
}
